import { useState, useEffect } from 'react'
import { Link, useSearch, useNavigate } from '@tanstack/react-router'
import { Loader2, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react'
import { BrandLogo } from '../components/BrandLogo'

type ResetStatus = 'loading' | 'valid' | 'invalid' | 'expired' | 'used' | 'success'

export function ResetPasswordPage() {
  const search = useSearch({ strict: false }) as { token?: string }
  const token = search.token
  const navigate = useNavigate()
  
  const [status, setStatus] = useState<ResetStatus>('loading')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setStatus('invalid')
      return
    }

    const validateToken = async () => {
      try {
        const res = await fetch(`/api/v1/auth/reset-password/${token}`)
        const data = await res.json()
        
        if (res.ok) {
          setEmail(data.email)
          setName(data.name)
          setStatus('valid')
        } else if (data.code === 'TOKEN_USED') {
          setStatus('used')
        } else if (data.code === 'TOKEN_EXPIRED') {
          setStatus('expired')
        } else {
          setStatus('invalid')
        }
      } catch {
        setStatus('invalid')
      }
    }

    validateToken()
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    
    setIsLoading(true)
    
    try {
      const res = await fetch('/api/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })
      
      const data = await res.json()
      
      if (res.ok) {
        setStatus('success')
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate({ to: '/login' })
        }, 2000)
      } else {
        setError(data.error || 'Something went wrong')
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Validating your reset link...</p>
        </div>
      </div>
    )
  }

  // Error states
  if (status === 'invalid' || status === 'expired' || status === 'used') {
    const errorMessages = {
      invalid: {
        title: 'Invalid reset link',
        message: 'This password reset link is invalid or has been corrupted.',
      },
      expired: {
        title: 'Link expired',
        message: 'This password reset link has expired. Please request a new one.',
      },
      used: {
        title: 'Link already used',
        message: 'This password reset link has already been used.',
      },
    }
    
    const { title, message } = errorMessages[status]
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl border border-border p-8">
            <Link to="/" className="inline-flex mb-8"><BrandLogo /></Link>
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h1 className="mt-4 text-2xl font-bold text-gray-900">{title}</h1>
            <p className="mt-2 text-gray-600">{message}</p>
            <Link
              to="/forgot-password"
              className="mt-6 inline-block px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl border border-border p-8">
            <Link to="/" className="inline-flex mb-8"><BrandLogo /></Link>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="mt-4 text-2xl font-bold text-gray-900">Password reset</h1>
            <p className="mt-2 text-gray-600">
              Your password has been reset successfully.
            </p>
            <p className="mt-4 text-sm text-gray-500">
              Redirecting to login...
            </p>
            <Loader2 className="w-6 h-6 animate-spin text-primary-600 mx-auto mt-2" />
          </div>
        </div>
      </div>
    )
  }

  // Reset form
  return (
    <div className="auth-page min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo */}
        <div className="flex flex-col items-center">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size="lg" />
          </Link>
          <h1 className="mt-8 text-3xl font-bold text-gray-900 text-center">
            Reset your password
          </h1>
          <p className="mt-2 text-sm text-gray-600 text-center">
            Hi <strong>{name}</strong>, enter your new password below.
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              disabled
              className="appearance-none relative block w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed sm:text-sm"
            />
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              New password
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="At least 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                title={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Confirm your new password"
            />
            {confirmPassword && password !== confirmPassword && (
              <p className="mt-1 text-sm text-red-500">Passwords don't match</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || password !== confirmPassword}
            className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Reset password'
            )}
          </button>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-500 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}
